import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import {
  buildEntrySearchParams,
  searchFplEntries,
  type FplEntrySearchResult
} from '@api-service/fpl-entries';

type TeamSearchProps = {
  placeholder: string;
  selectButtonText: string;
  /** Ties the input to a label the caller renders above it. */
  inputId?: string;
  /** Rendered between the input and the CTA, for hint copy owned by the step. */
  inputFooter?: ReactNode;
  /** Optional explicit search button. Omit to rely on the live debounced
      team-name search only (e.g. the GYTR import step); an FPL ID always
      needs an explicit submit, which this component provides its own
      control for when no CTA is given. */
  ctaText?: string;
  /**
   * Hides the CTA without giving up the submit control it stands for.
   *
   * Separate from omitting `ctaText`, because that would hand the ID submit
   * back to this component and put a second button on a screen whose caller has
   * deliberately taken it away (FFH-2742).
   */
  isCtaHidden?: boolean;
  /**
   * Called when a finished search turns up nothing, and again when that stops
   * being true. The results live here, but a caller may need to rearrange the
   * step around an empty one.
   */
  onEmptyResultsChange?: (hasEmptyResults: boolean) => void;
  onSelect: (entry: FplEntrySearchResult) => void;
};

const SEARCH_DEBOUNCE_MS = 500;

/**
 * FPL team search against the public /fpl/entries endpoint. A team name is
 * searched live as the visitor types; an FPL ID is searched only when they
 * submit it, because an ID is an exact-match lookup where a prefix is a
 * different team rather than a partial match.
 */
const TeamSearch: FC<TeamSearchProps> = ({
  placeholder,
  selectButtonText,
  inputId,
  inputFooter,
  ctaText,
  isCtaHidden = false,
  onEmptyResultsChange,
  onSelect
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FplEntrySearchResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const latestSearchRef = useRef(0);

  const runSearch = useCallback(async (rawQuery: string) => {
    const params = buildEntrySearchParams(rawQuery);
    if (!params) {
      setResults(null);
      setIsSearching(false);
      return;
    }

    const searchId = ++latestSearchRef.current;
    setIsSearching(true);
    setSearchError(null);

    try {
      const entries = await searchFplEntries(params);
      if (searchId !== latestSearchRef.current) return;
      setResults(entries);
    } catch {
      if (searchId !== latestSearchRef.current) return;
      setSearchError("Couldn't search right now. Please try again.");
    } finally {
      if (searchId === latestSearchRef.current) setIsSearching(false);
    }
  }, []);

  const searchParams = buildEntrySearchParams(query);
  const isIdQuery = searchParams !== null && 'fplId' in searchParams;

  const submitSearch = useCallback(() => runSearch(query), [runSearch, query]);

  // Debounced live search as the visitor types a team name. An FPL ID is
  // excluded: every prefix of one is itself a valid ID, so searching as it is
  // typed shows the teams owning those prefixes rather than a closer match.
  useEffect(() => {
    // Derived here rather than from the render scope: buildEntrySearchParams
    // returns a fresh object each render, so depending on it would restart the
    // debounce on every unrelated re-render.
    const params = buildEntrySearchParams(query);
    if (!params || 'fplId' in params) {
      setResults(null);
      setSearchError(null);
      return;
    }

    // The full result limit matters most here: team names cluster heavily, so a
    // shortlist hides the visitor's own team behind near-identical names.
    const timer = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const hasEmptyResults =
    !isSearching && results !== null && results.length === 0;

  useEffect(() => {
    onEmptyResultsChange?.(hasEmptyResults);
  }, [hasEmptyResults, onEmptyResultsChange]);

  const isCtaEnabled = searchParams !== null && !isSearching;
  // A numeric query has no live search to fall back on, so it needs a submit
  // control of its own wherever the caller has not supplied a CTA.
  const shouldShowIdSubmit = isIdQuery && !ctaText;
  const hasVisibleResults = results !== null && results.length > 0;

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          submitSearch();
        }}
        placeholder={placeholder}
        id={inputId}
        data-cy="readiness-team-search-input"
        className="w-full rounded-xl border-2 border-primary-400 bg-white px-[18px] py-3.5 font-body text-sm text-neutral-800 outline-none transition-all focus:border-primary-600"
      />

      {shouldShowIdSubmit && (
        <button
          type="button"
          onClick={submitSearch}
          disabled={isSearching}
          data-cy="readiness-team-search-id-submit"
          className="mt-3 w-full rounded-lg bg-primary-300 px-4 py-2.5 font-heading text-sm font-bold text-neutral-900 transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {isSearching ? 'Searching…' : 'Search this FPL ID'}
        </button>
      )}

      {inputFooter}

      {searchError && (
        <p className="mt-3 font-body text-sm text-danger-600">{searchError}</p>
      )}

      {isSearching && !hasVisibleResults && (
        <div className="flex justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
        </div>
      )}

      {hasEmptyResults && (
        <p className="mt-3 font-body text-sm text-neutral-500">
          No teams found. Try the exact team name, or your FPL ID.
        </p>
      )}

      {results !== null && results.length > 0 && (
        <ul
          // Kept on screen while the next search runs, dimmed rather than
          // unmounted, so the list does not flicker between searches.
          aria-busy={isSearching}
          className={`mt-3 space-y-3 transition-opacity ${
            isSearching ? 'opacity-60' : ''
          }`}
        >
          {results.map((entry) => (
            <li
              key={entry.fplEntryId}
              className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-neutral-100 px-4 py-3"
            >
              <div className="min-w-0 text-left">
                <p className="truncate font-heading text-sm font-bold text-neutral-800">
                  {entry.teamName}
                </p>
                <p className="truncate font-body text-xs text-neutral-500">
                  ID: {entry.fplEntryId} &middot; {entry.managerName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onSelect(entry)}
                data-cy={`readiness-team-search-select-${entry.fplEntryId}`}
                className="shrink-0 rounded-lg bg-primary-300 px-4 py-2 font-heading text-sm font-bold text-neutral-900 transition-colors hover:bg-primary-400"
              >
                {selectButtonText}
              </button>
            </li>
          ))}
        </ul>
      )}

      {ctaText && !isCtaHidden && (
        <div className="mt-6">
          <CtaButton
            onClick={() => runSearch(query)}
            disabled={!isCtaEnabled}
            dataCy="readiness-team-search-cta"
          >
            {ctaText}
          </CtaButton>
        </div>
      )}
    </div>
  );
};

export default TeamSearch;
